/*
 * "My store" selector (snippets/store-selector.liquid).
 *
 * Shows the saved store; it does not choose one. With no store saved the
 * control is a plain link to the store locator, where every card has "Make
 * This My Store". Once a store is saved, the control becomes a button that
 * opens a small panel about that store, and "Change store" goes back to the
 * locator.
 *
 * Two instances can be on a page: one in the utility bar (a popover) and one
 * in the mobile menu drawer (opens in place). Both read the same saved store
 * from store-search.js and both follow the `store:selected` event, so a store
 * saved on the locator or a store page updates every copy at once.
 *
 * This file is loaded once per copy, so the class sits inside a guard: a
 * second top-level `class StoreSelector` would be a syntax error.
 */
if (!customElements.get('store-selector')) {
  class StoreSelector extends HTMLElement {
    connectedCallback() {
      if (this.ready) return;
      this.ready = true;

      this.toggle = this.querySelector('[data-selector-toggle]');
      this.panel = this.querySelector('[data-selector-panel]');
      this.search = window.TandyStoreSearch;
      this.inline = this.dataset.context === 'drawer';

      if (!this.toggle || !this.panel || !this.search) return;

      this.toggle.addEventListener('click', event => {
        // No store yet: let the link go to the locator.
        if (!this.search.getSelected()) return;
        event.preventDefault();
        this.isOpen() ? this.close() : this.open();
      });
      this.toggle.addEventListener('keydown', event => {
        // Space activates a button but not a link; it is a button once a store is saved.
        if (event.key === ' ' && this.search.getSelected()) {
          event.preventDefault();
          this.toggle.click();
        }
      });

      this.querySelector('[data-selector-close]').addEventListener('click', () => this.close(true));
      this.addEventListener('keydown', event => {
        if (event.key === 'Escape' && this.isOpen()) { event.stopPropagation(); this.close(true); }
      });
      // A click anywhere else closes the popover. The drawer copy stays put: it
      // is part of the menu, not floating over the page.
      document.addEventListener('click', event => {
        if (!this.inline && this.isOpen() && !this.contains(event.target)) this.close();
      });

      // Saved (or cleared) somewhere: the locator, a store page, another tab.
      window.addEventListener('store:selected', () => this.render());
      window.addEventListener('storage', event => {
        if (event.key === this.search.SELECTED_KEY) this.render();
      });

      this.render();
    }

    isOpen() {
      return !this.panel.hidden;
    }

    open() {
      const store = this.search.getSelected();
      if (!store) return;
      this.fill(store);
      this.panel.hidden = false;
      this.classList.add('is-open');
      this.toggle.setAttribute('aria-expanded', 'true');
      // Hours and closures change; the saved copy is a snapshot from the day it
      // was picked. Refresh it from the directory now that someone is looking.
      this.refresh();
    }

    close(returnFocus) {
      this.panel.hidden = true;
      this.classList.remove('is-open');
      if (this.toggle.hasAttribute('aria-expanded')) this.toggle.setAttribute('aria-expanded', 'false');
      if (returnFocus) this.toggle.focus();
    }

    /* The control itself: a link to the locator, or a button for the panel. */
    render() {
      const store = this.search.getSelected();
      this.querySelector('[data-selector-name]').textContent =
        store ? this.search.placeName(store.location_name) : 'Find a store';
      this.classList.toggle('has-store', Boolean(store));

      if (store) {
        this.toggle.setAttribute('role', 'button');
        this.toggle.setAttribute('aria-expanded', this.isOpen() ? 'true' : 'false');
        if (this.isOpen()) this.fill(store);
      } else {
        this.toggle.removeAttribute('role');
        this.toggle.removeAttribute('aria-expanded');
        if (this.isOpen()) this.close();
      }
    }

    fill(store) {
      const set = (name, text) => {
        const el = this.querySelector(`[data-saved-${name}]`);
        el.textContent = text || '';
        el.hidden = !text;
        return el;
      };
      set('name', this.search.placeName(store.location_name));
      const number = this.search.storeNumber(store.location_name);
      set('number', number ? `Store #${number}` : '');
      set('address', StoreSelector.addressLines(store.address));
      set('today', this.search.todayText(store));

      const phone = set('phone', store.phone);
      if (store.phone) phone.href = 'tel:' + String(store.phone).replace(/[^\d+]/g, '');

      this.querySelector('[data-saved-page]').href = store.website || this.dataset.locatorUrl;

      // Straight to the store page's class schedule, for a store that runs them.
      const classes = this.querySelector('[data-saved-classes]');
      const hasClasses = Boolean(store.acuity_calendar_id && store.website);
      classes.hidden = !hasClasses;
      if (hasClasses) classes.href = store.website + '#classes';
    }

    /* "Street, Suite, City, ST ZIP" as street over city, like a locator card.
       The last two comma parts are the city line; anything shorter is left
       alone. The CSS shows the line break (white-space: pre-line). */
    static addressLines(address) {
      const parts = String(address || '').split(',').map(part => part.trim()).filter(Boolean);
      if (parts.length < 3) return parts.join(', ');
      return parts.slice(0, -2).join(', ') + '\n' + parts.slice(-2).join(', ');
    }

    /* Swap the saved snapshot for the directory's current record of the same
       store. Quiet on failure: the snapshot is still a correct address. */
    async refresh() {
      const saved = this.search.getSelected();
      if (!saved || !saved.store_id) return;
      try {
        const stores = await this.search.loadDirectory(this.dataset.directorySrc);
        const fresh = (stores || []).find(s =>
          String(s.store_id || '').toUpperCase() === String(saved.store_id).toUpperCase());
        const current = this.search.getSelected();
        if (!fresh || !current || current.store_id !== saved.store_id || !this.isOpen()) return;
        this.fill(Object.assign({}, current, fresh));
      } catch (err) {
        console.error('store-selector: could not refresh the saved store', err);
      }
    }
  }

  customElements.define('store-selector', StoreSelector);
}
